'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getMemoryAccessToken, tryAdminTokenRefresh } from '@/lib/api';
import type { AdminBadgeKey, AdminBadgeMap } from '@/lib/admin-badges';
import { invalidateAdminBadgeCache, resolveAdminBadges } from '@/lib/admin-badges';

function isAccessTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = Number(payload?.exp ?? 0);
    return !exp || exp <= Math.floor(Date.now() / 1000) + 30;
  } catch {
    return true;
  }
}

type UseAdminBadgesOptions = {
  enabled?: boolean;
  refreshMs?: number;
};

function badgesEqual(a: AdminBadgeMap, b: AdminBadgeMap): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if ((a[key as AdminBadgeKey] ?? 0) !== (b[key as AdminBadgeKey] ?? 0)) return false;
  }
  return true;
}

export function useAdminBadges(
  requestedKeys: AdminBadgeKey[],
  options: UseAdminBadgesOptions = {}
) {
  const { enabled = true, refreshMs = 60_000 } = options;
  const keys = useMemo(() => Array.from(new Set(requestedKeys)), [requestedKeys]);
  const [badges, setBadges] = useState<AdminBadgeMap>({});
  const [loading, setLoading] = useState(false);
  const authStoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      authStoppedRef.current = false;
      return;
    }
    if (keys.length === 0 || authStoppedRef.current) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let backoffMs = refreshMs;

    const scheduleNext = (delayMs: number) => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        void run();
      }, delayMs);
    };

    const stop = () => {
      cancelled = true;
      authStoppedRef.current = true;
      if (timer) clearInterval(timer);
      timer = null;
      setLoading(false);
    };

    const run = async () => {
      if (cancelled || authStoppedRef.current) return;
      setLoading(true);
      try {
        // UX-001: refresh short-lived access JWT before badge poll if near expiry.
        if (isAccessTokenExpired(getMemoryAccessToken())) {
          await tryAdminTokenRefresh();
        }
        const next = await resolveAdminBadges(keys);
        if (!cancelled && !authStoppedRef.current) {
          setBadges((prev) => (badgesEqual(prev, next) ? prev : { ...prev, ...next }));
          backoffMs = refreshMs;
        }
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) {
          stop();
          return;
        }
        if (status === 429 && !cancelled && !authStoppedRef.current) {
          invalidateAdminBadgeCache();
          const nextBackoff = Math.min(backoffMs * 2, 5 * 60_000);
          if (nextBackoff !== backoffMs) {
            backoffMs = nextBackoff;
            scheduleNext(backoffMs);
          }
        }
      } finally {
        if (!cancelled && !authStoppedRef.current) {
          setLoading(false);
        }
      }
    };

    void run();
    scheduleNext(refreshMs);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [enabled, keys, refreshMs]);

  return { badges, loading };
}
